import {useState} from "react";
import {FiExternalLink, FiSearch} from "react-icons/fi";
import {Link} from "react-router";

import {ActionBarWrapper} from "@/components/actionBarWrapper/ActionBarWrapper.tsx";
import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {EmptyTable} from "@/components/table/subComponents/EmptyTable/EmptyTable.tsx";
import {Table} from "@/components/table/Table.tsx";
import {ColumnSpec, Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {IntegrationsItems} from "@/lib/queries/integrations.ts";
import {IntegrationListData} from "@/lib/types/integrations.ts";
import {
  getIntegrationModuleInfo,
  IntegrationModuleInfo,
  IntegrationModules,
  isKnownIntegrationModule,
} from "@/pages/integrations/integrations.types.ts";
import {IntegrationActions} from "@/pages/integrations/list/integrationsActive/actions/IntegrationActions.tsx";
import {IntegrationsActiveRow} from "@/pages/integrations/list/integrationsActive/IntegrationsActiveRow.tsx";

type IntegrationsTableProps = {
  integrations: IntegrationsItems;
  sortColumn: string;
  sortDirection: SortingDirection;
  onSortingChange?: (sorting: Sorting) => void;
  setTab: (tab: string) => void;
};

export const IntegrationsActive = ({
  integrations,
  sortColumn = "name",
  sortDirection,
  onSortingChange,
  setTab,
}: IntegrationsTableProps) => {
  const {user: currentUser} = useCurrentUserContext();
  const [search, setSearch] = useState("");

  const knownIntegrations = integrations.items.filter((integration) =>
    isKnownIntegrationModule(integration.module)
  ) as IntegrationListData[];

  const activeIntegrations = knownIntegrations.filter((integration) => {
    const searchLower = search.toLowerCase();
    return (
      integration.name.toLowerCase().includes(searchLower) ||
      IntegrationModuleInfo[integration.module as IntegrationModules].label.toLowerCase().includes(searchLower)
    );
  });

  const tableSpec: ColumnSpec<IntegrationListData>[] = [
    ...[
      {
        fieldName: "name",
        fieldTitle: "Name",
        enableSorting: true,
        accessor: (integration: IntegrationListData) => (
          <IntegrationsActiveRow integration={integration} currentUser={currentUser!} />
        ),
      },
      {
        fieldName: "module",
        fieldTitle: "Provider",
        accessor: (integration: IntegrationListData) => getIntegrationModuleInfo(integration.module)?.label ?? "",
        enableSorting: true,
      },
    ],
    ...(currentUser?.has_write_access
      ? [
          {
            fieldName: "actions",
            meta: {columnClassName: "text-end"},
            accessor: (integration: IntegrationListData) => <IntegrationActions integration={integration} />,
          },
        ]
      : []),
  ];

  if (!integrations) return <LoadingContainer />;

  return (
    <>
      <ActionBarWrapper
        className={"pb-4"}
        left={<SearchInput value={search} onChange={setSearch} variant={"tableControlBar"} />}
        right={
          <Button
            variant={"secondary"}
            className={"text-nowrap"}
            as={"a"}
            href={"https://support.uptime.com/hc/en-us/articles/115002534509-Overview-of-Push-Notifications"}
            target={"_blank"}
          >
            Support Docs <Icon icon={FiExternalLink} />
          </Button>
        }
      />

      <Table
        rows={activeIntegrations}
        sorting={{column: sortColumn, direction: sortDirection}}
        onSortingChange={onSortingChange}
        specs={tableSpec}
        initialState={{columnPinning: {right: ["actions"]}}}
        noDataComponent={
          <EmptyTable>
            <div className={"m-4 d-flex flex-column align-items-center justify-content-center gap-2"}>
              <Icon icon={FiSearch} className={"mb-4"} />
              <span className={"pulseTextH3Bold"}>You don’t have any Active Integrations yet.</span>
              <span>
                Go to{" "}
                <Link to={"/integrations/manage/?tab=library"} onClick={() => setTab("library")}>
                  Library
                </Link>{" "}
                to explore our available Integrations.
              </span>
            </div>
          </EmptyTable>
        }
      />
      <div className={"pulseTextBodySmall pt-2"}>
        {knownIntegrations.length} Total {knownIntegrations.length == 1 ? "Entry" : "Entries"}
      </div>
    </>
  );
};
